import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
    fastTranscriptionApi,
    createInterviewApi,
    uploadInterviewAudioApi,
    finaliseInterviewApi,
    getInterviewStatusApi,
    reportUploadFailureApi,
    classifyUploadFailure,
} from '../../api/api'
import type { InterviewJob, InterviewStatus } from '../../api/api'
import styles from './micprototype.module.css'
import { LiveTranscriptionSession } from '../../audio/liveTranscription'
import { useAuth } from '../../hooks/useAuth'
import {
    CircularProgressbarWithChildren,
    buildStyles,
} from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import {
    Checkmark24Filled,
    Dismiss12Regular,
    Mic24Regular,
} from '@fluentui/react-icons'
import ToolTip from '../Tooltip/Tooltip'

interface micPrototypeProps {
    updateQuestion: React.Dispatch<React.SetStateAction<string>>
    setIsTranscribing: React.Dispatch<React.SetStateAction<boolean>>
    isRecording: boolean
    setIsRecording: React.Dispatch<React.SetStateAction<boolean>>
    micLanguageLocale: string
    chatType: 'ask' | 'contribute'
    setSendingTranscription: React.Dispatch<React.SetStateAction<boolean>>
    appendTranscription: (text: string) => void // ADD THIS
    micPermissionsDenied?: boolean
    /**
     * Record that the text now in the box came from speech.
     *
     * Live dictation cannot go through `appendTranscription`: interim
     * transcripts supersede each other, so it has to replace rather than append
     * and therefore writes via `updateQuestion`. That bypassed the only thing
     * setting the voice flag, so a contribution that was entirely dictated was
     * submitted as `inputSource: 'text'` and counted as typed in
     * `contributions_by_input_source_total`.
     */
    markVoiceInput?: () => void
}
// Every way transcription can end badly has to reach the contributor — otherwise
// the spinner just stops and no text ever appears. Named here rather than inline
// because the upload failure is reported from two different call sites.
const ERR_UPLOAD =
    "We couldn't send that recording for transcription. Check your connection and try again."
const ERR_SILENT =
    "We couldn't hear anything in that recording. Please try again."
const ERR_FAILED = "We couldn't transcribe that recording. Please try again."
const ERR_TIMEOUT =
    'Transcribing is taking longer than expected and we lost track of it. ' +
    'Please try recording again.'
// Partial coverage used to be reported here too, as "Live captions missed part of
// your recording". It is not, and the client no longer measures coverage at all.
//
// Two reasons, and the second is the one that makes this safe. First, on a healthy
// upload the warning is about text the pipeline replaces anyway: chirp_3 transcribes
// the whole recording and supersedes the preview, so short caption text fixes itself
// and the contributor was asked to check something that was about to be overwritten.
// It only described a real loss when the audio ALSO failed to arrive — and that case
// is now counted, at `audio_upload_failures_total`.
//
// Second, the measurement is not lost with the message. `_record_stream_outcome` in
// `api/routes/speech_stream.py` observes `speech_stream_coverage_ratio` for EVERY
// session, against the same denominator this used: the recorded seconds the client
// sends with `stop`. That histogram exists for the shape of its tail below 0.9, which
// is precisely the question this warning was answering one contributor at a time.
//
// `LiveTranscriptionSession.coverage()` therefore has no caller left. It is kept
// because it is a self-contained accessor and the socket's own tests cover it.

export interface MicrophoneHandle {
    cancelRecording: () => void
    /**
     * The recording these words were captured against, or null.
     *
     * Read at submit so the contribution can point at its audio. Without that link a
     * transcript landing later has no way to find the contribution it should improve,
     * which is the whole mechanism the fallback rests on.
     */
    capturedRecordingId: () => string | null
}

const MicrophonePrototype = React.forwardRef<
    MicrophoneHandle,
    micPrototypeProps
>(function MicrophonePrototype(
    {
        micLanguageLocale,
        updateQuestion,
        setIsTranscribing,
        isRecording,
        setIsRecording,
        chatType = 'contribute',
        setSendingTranscription,
        appendTranscription,
        micPermissionsDenied,
        markVoiceInput,
    }: micPrototypeProps,
    ref
) {
    // The socket carries the tenant as a query param because a WebSocket cannot
    // set headers. Omitting it left the server to fall back to the anonymous
    // session cookie, so a signed-in contributor on a tenantless JWT was closed
    // with 4403 and — with no onError below — saw nothing at all.
    const { activeTenant } = useAuth()
    const mediaStream = useRef<MediaStream | null>(null)
    const mediaRecorder = useRef<MediaRecorder | null>(null)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null) //store the time
    const chunks = useRef<Blob[]>([])
    // Set by cancelRecording so `onstop` discards the audio instead of transcribing
    // it. `stop()` fires `onstop` either way, so cancelling needs a flag, not just
    // a different call.
    const cancelledRef = useRef(false)
    // Live dictation: words go straight into the text box as they are spoken.
    const liveSessionRef = useRef<LiveTranscriptionSession | null>(null)
    // Whatever was in the box when recording started; dictation appends to it.
    const dictationBaseRef = useRef('')
    // Whether streaming delivered any words. When it did not — no AudioWorklet,
    // blocked socket, silent recording — the post-recording transcription runs as
    // a fallback so the contributor is not left with nothing.
    const heardSomethingRef = useRef(false)
    // When capture began, so the socket can tell the server how long the recording
    // actually was. `timeLeft` is not usable for this: it is state, it is throttled
    // by the render cycle, and it stops being accurate the moment a render is
    // skipped.
    const recordingStartedAtRef = useRef<number | null>(null)
    // The recording reserved *before* capture starts, so the live socket can attribute
    // its words to it and the contribution can point at it. Null when the reservation
    // failed — capture still runs, it is simply unattributed.
    const reservedRecordingRef = useRef<string | null>(null)
    // Scoped to ONE take, so a retried reserve within that take returns the same row
    // instead of orphaning one at `pending_upload`.
    //
    // "One take" is the correction. This was minted with `??` and never cleared, so it
    // was really scoped to the component *mount*: every take after the first replayed
    // the first take's key, was handed back the first take's recording, PUT its audio
    // over that recording's blob and then 409'd at commit, because that row was long
    // since `completed`. The contributor saw "we couldn't save the audio" and their
    // words were filed against someone else's recording.
    //
    // Measured at the British Motor Show on 2026-08-21, the first event to run this
    // path: 299 reservations produced 62 rows, 237 were replays, 219 of 275 commits
    // (79.6%) were rejected, and 206 of 258 voice contributions ended up pointing at
    // audio that was not their own. Because each replay re-signed the SAME object
    // path, the surviving blob is the last take while the transcript is the first
    // take's — one row held a 4 MB object against a recorded duration of 50 seconds.
    const idempotencyKeyRef = useRef<string | null>(null)
    // Where to PUT, learned at reserve rather than at stop.
    const uploadOfferRef = useRef<{
        url: string
        offer: InterviewJob['upload']
    } | null>(null)
    // Stops the transcript poll: set on unmount and on cancel. The poll is a
    // sleep chain with a six-minute deadline and no way to abort mid-flight, so
    // nothing otherwise prevented it running on after the component was gone.
    const abandonedRef = useRef(false)
    const isAskChat = chatType === 'ask'
    const tooltipText = isAskChat ? 'Record question' : 'Record contribution'
    const maxRecordingTime = isAskChat ? 60 : 1800

    // Seeded from maxRecordingTime, not a literal 30: the contribute cap is 1800
    // and toggleRecording only corrected it on the first press, so the progress
    // ring rendered against the wrong denominator until then.
    const [timeLeft, setTimeLeft] = useState(maxRecordingTime)
    // Server-side interview transcription is async, so every way it can end
    // badly (failed, gave up waiting, network) has to reach the contributor —
    // otherwise the spinner just stops and no transcript ever appears.
    const [interviewError, setInterviewError] = useState<string | null>(null)

    // The countdown interval too: it was cleared only by stopRecording, so
    // unmounting mid-recording left it ticking setTimeLeft on a dead component —
    // in vitest, after jsdom was torn down ("window is not defined").
    useEffect(
        () => () => {
            abandonedRef.current = true
            if (timerRef.current !== null) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        },
        []
    )

    //toggle recording
    function toggleRecording() {
        if (isRecording) {
            stopRecording()
            setIsTranscribing(false)
        } else {
            if (micPermissionsDenied) {
                return
            }

            setTimeLeft(maxRecordingTime) // Reset the countdown timer to 30 seconds
            timerRef.current = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        if (timerRef.current !== null) {
                            clearInterval(timerRef.current)
                        }
                        setTimeout(() => {
                            setIsRecording(false)
                            stopRecording()
                        }, 0)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
            startRecording()
            setIsTranscribing(true)
        }
    }

    /**
     * Dictate into the text box: words appear where the contributor is already
     * looking, as they speak.
     *
     * There is deliberately only ONE place text ever appears. An earlier version
     * showed live words in a floating callout and then, ~20s after the speaker
     * stopped, replaced them with the server's transcript in the box — two
     * surfaces and a jump, which read as disjointed. The server still produces the
     * better transcript; it just does so without moving anything under the user.
     *
     * Never throws into the caller: an old browser without AudioWorklet, a blocked
     * socket, or a provider that cannot stream falls back to the post-recording
     * transcription path instead (see `onstop`).
     */
    const startLiveDictation = (
        stream: MediaStream,
        recordingId: string | null,
        isRetry = false
    ) => {
        // Anything already typed stays put; dictation appends to it rather than
        // replacing it, so a contributor can type and then speak.
        updateQuestion((existing) => {
            dictationBaseRef.current = existing
            return existing
        })

        const session = new LiveTranscriptionSession({
            stream,
            locale: micLanguageLocale || 'en-GB',
            tenantSlug: activeTenant?.slug ?? null,
            // Attributes the socket's words to the reserved recording, so a closed tab
            // or a failed upload no longer loses them. Absent when reserving failed —
            // the socket works identically, the words just have nowhere to persist.
            recordingId,
            onUpdate: (text) => {
                if (text) {
                    heardSomethingRef.current = true
                    // Only once words actually arrive: opening the socket is
                    // not evidence the contributor said anything.
                    markVoiceInput?.()
                }
                const base = dictationBaseRef.current
                updateQuestion(base ? `${base} ${text}` : text)
            },
            // Still silent on screen: the "live captions stopped" callout blocked
            // the contribute UI at events, and the recording is still uploaded and
            // transcribed on stop. But no longer a dead end — the preview gets ONE
            // fresh socket, so a blip does not blank the captions for the rest of
            // the take. The retry carries no recordingId: the server would store
            // only the second socket's words over the first's.
            onError: () => {
                if (isRetry || liveSessionRef.current !== session) return
                if (!mediaStream.current) return
                void session.stop()
                startLiveDictation(stream, null, true)
            },
        })
        liveSessionRef.current = session
        session.start().catch(() => {
            liveSessionRef.current = null
        })
    }

    const stopLiveDictation = () => {
        const session = liveSessionRef.current
        liveSessionRef.current = null
        if (!session) return

        const startedAt = recordingStartedAtRef.current
        const recordedSeconds = startedAt ? (Date.now() - startedAt) / 1000 : 0
        recordingStartedAtRef.current = null

        // The denominator for the server's coverage histogram, and now the only
        // coverage measurement anywhere: `speech_stream_coverage_ratio` is observed
        // against it for every session. Computed here rather than in the recorder's
        // `onstop` because the teardown order is `stopLiveDictation()` then
        // `mediaRecorder.stop()` — by the time `onstop` runs, this session is gone.
        void session.stop(recordedSeconds || undefined)
    }

    /**
     * Reserve the recording this capture will upload to, before anything is captured.
     *
     * Returns its id, or null if reserving failed. Null is a degraded mode, not an
     * error: the mic keeps working, the words still reach the box and still submit —
     * they simply arrive with no audio behind them, which is exactly what happens today
     * on every recording.
     *
     * Declares `preview_with_fallback`: this client streams *and* records, and the
     * audio is insurance against the stream falling short. That intent is what stops
     * the server reaping the reservation as a failed upload while a contributor is
     * still talking.
     */
    const reserveRecording = async (): Promise<string | null> => {
        if (isAskChat) return null
        try {
            idempotencyKeyRef.current =
                idempotencyKeyRef.current ?? crypto.randomUUID()
            const job = await createInterviewApi({
                filename: 'interview.webm',
                language: micLanguageLocale || 'en-GB',
                capture_intent: 'preview_with_fallback',
                idempotency_key: idempotencyKeyRef.current,
                // No `file_size_bytes`: nothing has been recorded yet. The server reads
                // unknown size as "offer a resumable session", which is the right answer
                // for live capture — a recording in progress does not know its length,
                // and that is precisely the upload that must survive a dropped link.
            })
            reservedRecordingRef.current = job.task_id
            uploadOfferRef.current = { url: job.upload_url, offer: job.upload }
            return job.task_id
        } catch (error) {
            console.error(
                'Could not reserve a recording; capturing unattributed',
                error
            )
            // The shape with no residue anywhere else: with no reservation there is no
            // `recordings` row, so nothing downstream — not the reaper, not a query over
            // `capture_resolution` — can later show that this attempt happened at all.
            reportUploadFailureApi({
                stage: 'reserve',
                reason: classifyUploadFailure(error),
            })
            reservedRecordingRef.current = null
            uploadOfferRef.current = null
            return null
        }
    }

    const startRecording = async () => {
        try {
            setInterviewError(null)
            cancelledRef.current = false
            heardSomethingRef.current = false
            abandonedRef.current = false
            // Per-take state, and the one that was missing from this list. Cleared
            // here rather than in `reserveRecording` so the `??` there still does its
            // job: within a single take a retried reserve must reuse the key, and it
            // is only *across* takes that reusing it is wrong.
            idempotencyKeyRef.current = null
            setIsRecording(true)
            // Explicit constraints, not `audio: true` (docs/adr/0020). A bare
            // request leaves echo cancellation, noise suppression and gain to
            // BROWSER defaults, which differ across Chrome/Safari/Firefox — so the
            // same room produced differently-processed audio per browser, for both
            // consumers of this stream (the live-preview worklet and the recorded
            // blob the pipeline transcribes). These pin Chrome's defaults
            // everywhere. Echo cancellation is also load-bearing on its own: it is
            // the belt-and-braces half of keeping TTS playback out of the mic
            // (docs/reference/sources/contribute-v2-api.md).
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                },
            })
            mediaStream.current = stream

            // **Reserve first.** ADR-0016 assumed this order and the client had it
            // backwards: the reservation used to happen after `onstop`, so at
            // socket-open time no recording existed, `?recordingId=` could not be sent,
            // and nothing linked the contribution to its audio. Both halves of the
            // fallback — the preview surviving a closed tab, and a transcript later
            // improving the words — depend on the id existing *now*.
            //
            // Deliberately not awaited into the failure path: if reserving fails the
            // contributor still records and still submits. They lose attribution and
            // the fallback, not their words.
            const reserved = await reserveRecording()
            startLiveDictation(stream, reserved)
            mediaRecorder.current = new MediaRecorder(stream)
            mediaRecorder.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.current.push(e.data)
                }
            }
            mediaRecorder.current.onstop = () => {
                const recordedBlob = new Blob(chunks.current, {
                    type: 'audio/webm',
                })
                chunks.current = []
                // Cancelled by the user — discard the audio and transcribe nothing.
                if (cancelledRef.current) {
                    cancelledRef.current = false
                    return
                }
                void handleRecordingStopped(recordedBlob)
            }

            recordingStartedAtRef.current = Date.now()
            mediaRecorder.current.start()
        } catch (error) {
            console.error('Error accessing microphone:', error)
        }
    }

    const stopRecording = () => {
        setIsRecording(false)
        stopLiveDictation()
        if (
            mediaRecorder.current &&
            mediaRecorder.current.state === 'recording'
        ) {
            mediaRecorder.current.stop()
        }
        if (mediaStream.current) {
            mediaStream.current.getTracks().forEach((track) => {
                track.stop()
            })
        }

        // Clear the timer and reset time left
        if (timerRef.current !== null) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        setTimeLeft(maxRecordingTime) // Reset to initial value
    }

    // Abandon the recording: stop the recorder and release the mic WITHOUT
    // transcribing. Exposed on the ref so the input boxes' cancel button can
    // reach it — previously that call hit a null ref, so cancelling left the
    // recorder running and the audio was transcribed anyway.
    const cancelRecording = () => {
        cancelledRef.current = true
        // Also abandon any transcript poll already in flight: cancelling used to
        // stop the recorder but leave a previous take's poll running, which
        // could then drop that transcript into the box.
        abandonedRef.current = true
        stopRecording()
        setIsTranscribing(false)
        setSendingTranscription(false)
        setInterviewError(null)
    }

    useImperativeHandle(ref, () => ({
        cancelRecording,
        capturedRecordingId: () => reservedRecordingRef.current,
    }))

    //send the blob webm audio to the api for transcription
    const sendToTranscriptionApi = async (blob: Blob) => {
        const formData = new FormData()
        formData.append('audio', blob, 'recording.webm')
        // The language every provider reads. `definition` below is Azure's shape and
        // GCP ignores it, so without this field every clip was transcribed as en-GB.
        formData.append('locale', micLanguageLocale || 'en-GB')
        formData.append(
            'definition',
            JSON.stringify({
                // Was hardcoded to en-GB, so this path ignored the language the
                // contributor had picked in the mic's own dropdown — the two
                // other transcription paths already honour it.
                locales: [micLanguageLocale || 'en-GB'],
                diarization: { maxSpeakers: 2, enabled: true },
            })
        )

        try {
            setSendingTranscription(true)
            // The backend now normalises every provider to a top-level `text`
            // (speaker grouping, when any, is applied server-side).
            const fastAPIresponse = await fastTranscriptionApi(formData)
            const text = fastAPIresponse.text ?? ''

            // Store this session's transcription

            // This gets the current transcription value and appends it
            appendTranscription(text)
        } catch (error) {
            console.error('Error sending audio to transcription API:', error)
        } finally {
            // In `finally`: a failed request left the spinner on "sending" forever.
            setSendingTranscription(false)
        }
    }

    /**
     * What happens once the recording ends.
     *
     * In the normal case: nothing the contributor can see. Their words are already
     * in the box, put there as they spoke, and nothing writes to the box after
     * this point — that late swap is exactly what made the flow feel disjointed.
     * A contribution is still uploaded so the server can transcribe it properly
     * (chirp_3, punctuation, and speaker attribution where diarization is enabled)
     * and keep that as the contribution's text.
     *
     * The old post-recording path survives as a FALLBACK, for when live dictation
     * delivered nothing — no AudioWorklet, a blocked socket, a silent recording.
     * There is no text to disturb in that case, so a late transcript is strictly
     * better than none.
     */
    const handleRecordingStopped = async (blob: Blob) => {
        if (isAskChat) {
            // Nothing to store server-side for a question; the sync endpoint is
            // only worth calling if streaming gave the asker nothing.
            if (!heardSomethingRef.current) await sendToTranscriptionApi(blob)
            return
        }

        // Unconditional, and it stays that way. The audio is the contribution's
        // safety net; nothing measured about the stream may be allowed to decide
        // whether we keep it.
        const taskId = await uploadContributionRecording(blob)

        // Two very different situations, and only one of them is the contributor's
        // to do anything about.
        //
        // Captions delivered nothing: the box is empty, the upload was the only
        // route to any text at all, and saying so is the whole of the help we can
        // offer. That is `ERR_UPLOAD`, and it stays.
        //
        // Captions delivered: their words are already in the box. What was lost is
        // the audio — the better transcript and the speaker labels — and there is no
        // action that recovers it. "Please re-record" asks them to spend another
        // sitting on the connection that just failed, and at a live event in August
        // 2026 this fired often enough on one bad venue link to become the thing
        // people were looking at instead of the thing they were saying. So it is
        // reported to us and not to them; `reportUploadFailureApi` is what replaces
        // the callout, and muting this without it would just restore the silence
        // that made the loss invisible before.
        if (!taskId) {
            if (heardSomethingRef.current) return
            setInterviewError(ERR_UPLOAD)
            return
        }

        // Nothing was captured live: the server transcript is the only text there
        // will be, so wait for it. When captions DID run there is nothing further to
        // do — their words are in the box, the audio is on its way, and the better
        // transcript replaces the preview server-side without the contributor being
        // asked to watch it happen.
        if (!heardSomethingRef.current) {
            await awaitContributionTranscript(taskId)
        }
    }

    /**
     * Hand the recording to the server and stop caring about the result.
     *
     * The server transcribes it properly — chirp_3 over the whole recording, with
     * punctuation and, where diarization is enabled, speaker attribution — and
     * keeps that as the contribution's text.
     *
     * Returns the task id so the fallback can poll it, or null if the upload
     * failed. Whether a failure is worth putting on the *screen* is the caller's
     * decision; whether it is worth putting on the *record* is not, and every path
     * out of here that loses the audio reports itself.
     */
    const uploadContributionRecording = async (
        blob: Blob
    ): Promise<string | null> => {
        // The recording was reserved before capture began, so this only sends and
        // commits. Reserving here as well would mint a second row for the same audio
        // and leave the first — the one the socket attributed its preview to —
        // stranded at `pending_upload` until the reaper failed it.
        const recordingId = reservedRecordingRef.current
        const target = uploadOfferRef.current
        // Already reported at `reserve`; reporting again here would double-count the
        // same lost sitting under a stage it never reached.
        if (!recordingId || !target) return null

        try {
            // `target.offer` is undefined unless the server offered a resumable session,
            // in which case this sends the recording in chunks instead of one PUT.
            await uploadInterviewAudioApi(target.url, blob, target.offer)
        } catch (error) {
            console.error('Error uploading contribution recording:', error)
            reportUploadFailureApi({
                stage: 'transfer',
                reason: classifyUploadFailure(error),
                recording_id: recordingId,
            })
            return null
        }

        try {
            await finaliseInterviewApi(recordingId)
        } catch (error) {
            // Split from the transfer above rather than sharing one catch, because the
            // two are not the same failure and the difference is the whole diagnosis.
            // `transfer` means the bytes are gone. `commit` means the bytes may well be
            // sitting in the bucket and only the confirmation was lost — the row is
            // still `pending_upload` and, with a resumable session, still resumable.
            // Collapsing them would report a recoverable upload as a lost one.
            console.error('Error committing contribution recording:', error)
            reportUploadFailureApi({
                stage: 'commit',
                reason: classifyUploadFailure(error),
                recording_id: recordingId,
            })
            return null
        }

        return recordingId
    }

    /**
     * Fallback only: wait for the server's transcript and put it in the box.
     *
     * Runs when live dictation produced nothing, so there is no text to disturb —
     * a late transcript beats no transcript.
     */
    const awaitContributionTranscript = async (taskId: string) => {
        const sleep = (ms: number) =>
            new Promise((resolve) => setTimeout(resolve, ms))

        /**
         * One poll, treating a transport failure as transient.
         *
         * A single 5xx or dropped connection used to throw straight out of the loop
         * and land in the catch below as `ERR_UPLOAD` — abandoning a perfectly healthy
         * six-minute wait because one request of roughly a hundred and forty blipped.
         * On the venue connections this whole feature exists for, that is the likely
         * outcome rather than the unlucky one.
         *
         * Reported as still processing, so the loop sleeps and asks again. The deadline
         * is what ends it, and a genuinely dead job still surfaces as `ERR_TIMEOUT`
         * rather than being retried forever.
         */
        const poll = async (id: string): Promise<InterviewStatus> => {
            try {
                return await getInterviewStatusApi(id)
            } catch (error) {
                console.warn(
                    'Transient error polling transcript; will retry',
                    error
                )
                return { status: 'processing' } as InterviewStatus
            }
        }
        try {
            setSendingTranscription(true)
            // Poll for the role-labelled transcript (cap the wait as a safety net).
            const deadline = Date.now() + 6 * 60 * 1000
            let status = await poll(taskId)
            while (
                status.status === 'processing' &&
                Date.now() < deadline &&
                !abandonedRef.current
            ) {
                await sleep(2500)
                if (abandonedRef.current) return
                status = await poll(taskId)
            }
            // Unmounted, or the contributor cancelled: stop without touching
            // state. This loop is a setTimeout chain with a six-minute deadline,
            // so without the check it outlived the component and wrote into it.
            if (abandonedRef.current) return

            if (status.status === 'completed' && status.transcript) {
                appendTranscription(status.transcript)
            } else if (status.status === 'completed') {
                setInterviewError(ERR_SILENT)
            } else if (status.status === 'failed') {
                console.error('Interview transcription failed:', status.error)
                setInterviewError(status.error || ERR_FAILED)
            } else {
                // Still 'processing' when we ran out of patience. The server keeps
                // going, but there's no way to rejoin this job, so say so plainly
                // rather than leaving the contributor with a stopped spinner.
                console.error('Interview transcription timed out')
                setInterviewError(ERR_TIMEOUT)
            }
        } catch (error) {
            console.error('Error transcribing interview contribution:', error)
            setInterviewError(ERR_UPLOAD)
        } finally {
            setSendingTranscription(false)
        }
    }

    return (
        <div className={styles.mainContainer}>
            {interviewError && (
                <div className={styles.errorCallout} role="alert">
                    <span>{interviewError}</span>
                    <button
                        type="button"
                        className={styles.errorDismiss}
                        aria-label="Dismiss transcription error"
                        onClick={() => setInterviewError(null)}
                    >
                        <Dismiss12Regular />
                    </button>
                </div>
            )}
            <ToolTip
                content={
                    micPermissionsDenied
                        ? 'Enable microphone to use voice input'
                        : tooltipText
                }
                side="top"
            >
                <span style={{ display: 'inline-block', lineHeight: 0 }}>
                    <button
                        className={`${styles.button} ${
                            isRecording ? styles.recordingButton : ''
                        } ${micPermissionsDenied ? styles.disabledButton : ''}`}
                        aria-disabled={micPermissionsDenied}
                        type="button"
                        onClick={() => {
                            toggleRecording()
                        }}
                    >
                        {isRecording ? (
                            <div
                                className="divWrapper"
                                style={{
                                    borderWidth: '2px',
                                    borderColor:
                                        'var(--color-interactive-primary-hover)',
                                    borderRadius: '50%',
                                    padding: '1px',
                                    display: 'inline-block',
                                    width: '35px',
                                    height: '35px',
                                    backgroundColor:
                                        'var(--color-interactive-primary-hover)',
                                }}
                            >
                                <CircularProgressbarWithChildren
                                    value={
                                        ((maxRecordingTime - timeLeft) /
                                            maxRecordingTime) *
                                        100
                                    }
                                    strokeWidth={6}
                                    background
                                    styles={buildStyles({
                                        strokeLinecap: 'round',
                                        pathTransitionDuration: 0.9,
                                        pathColor: 'var(--color-text-inverse)',
                                        trailColor: 'transparent',
                                        backgroundColor:
                                            'var(--color-interactive-primary-hover)',
                                    })}
                                >
                                    <Checkmark24Filled primaryFill="var(--color-text-inverse)" />
                                </CircularProgressbarWithChildren>
                            </div>
                        ) : (
                            <Mic24Regular
                                primaryFill={
                                    micPermissionsDenied
                                        ? 'var(--color-text-tertiary)'
                                        : 'var(--color-text-brand)'
                                }
                            />
                        )}
                    </button>
                </span>
            </ToolTip>
        </div>
    )
})
export default MicrophonePrototype
